import { useState, useEffect, useMemo } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, MapPin, Plus, Edit, Trash2, Save, X, Search, Upload, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import AreaMapEditor from './AreaMapEditor';

const DEFAULT_CATEGORIES = ['Sedan', 'SUV', 'Van', 'Luxury'];

export default function PricingRules() {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [areas, setAreas] = useState([]);
  const [rules, setRules] = useState([]);
  const [partnerCompanies, setPartnerCompanies] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');

  // Area form
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [areaName, setAreaName] = useState('');
  const [areaKeywords, setAreaKeywords] = useState('');
  const [areaCircles, setAreaCircles] = useState([]);
  const [areaPolygons, setAreaPolygons] = useState([]);
  const [areaDeleteId, setAreaDeleteId] = useState(null);

  // KML import
  const [kmlDialogOpen, setKmlDialogOpen] = useState(false);
  const [kmlUrl, setKmlUrl] = useState('');
  const [kmlPrefix, setKmlPrefix] = useState('');
  const [kmlReplaceExisting, setKmlReplaceExisting] = useState(false);
  const [kmlImporting, setKmlImporting] = useState(false);

  // Rule form
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    partner_company: '', vehicle_category: 'SEDAN',
    origin_area_id: '', destination_area_id: '', price: '',
  });
  const [ruleDeleteId, setRuleDeleteId] = useState(null);

  // Filters
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [areasRes, rulesRes, companiesRes, vehTypesRes] = await Promise.all([
        axios.get(`${API}/pricing-areas`, auth),
        axios.get(`${API}/pricing-rules`, auth),
        axios.get(`${API}/pricing-rules/partner-companies`, auth),
        axios.get(`${API}/settings/vehicle-types`, auth),
      ]);
      setAreas(areasRes.data || []);
      setRules(rulesRes.data || []);
      setPartnerCompanies(companiesRes.data || []);
      const vt = vehTypesRes.data?.value;
      if (Array.isArray(vt) && vt.length > 0) setVehicleTypes(vt);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const addCategory = async () => {
    const v = newCategoryInput.trim();
    if (!v) return;
    if (vehicleTypes.some(t => t.toLowerCase() === v.toLowerCase())) {
      toast.error('This category already exists');
      return;
    }
    const next = [...vehicleTypes, v];
    try {
      const res = await axios.put(`${API}/settings/vehicle-types`, { value: next }, auth);
      const updated = res.data?.value || next;
      setVehicleTypes(updated);
      setRuleForm(prev => ({ ...prev, vehicle_category: v }));
      setNewCategoryInput('');
      setAddingCategory(false);
      toast.success(`Category "${v}" added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const categories = useMemo(() => {
    const set = new Set(vehicleTypes);
    rules.forEach(r => r.vehicle_category && set.add(r.vehicle_category));
    return Array.from(set);
  }, [rules, vehicleTypes]);

  // ---------- Areas ----------
  const openAreaDialog = (area = null) => {
    setEditingArea(area);
    setAreaName(area?.name || '');
    setAreaKeywords((area?.keywords || []).join(', '));
    setAreaCircles(area?.circles || []);
    setAreaPolygons(area?.polygons || []);
    setAreaDialogOpen(true);
  };

  const saveArea = async () => {
    const name = areaName.trim();
    if (!name) return toast.error('Name is required');
    const keywords = areaKeywords.split(',').map(s => s.trim()).filter(Boolean);
    const circles = (areaCircles || [])
      .filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lng) && Number(c.radius_m) > 0)
      .map(c => ({ lat: c.lat, lng: c.lng, radius_m: Number(c.radius_m) }));
    const polygons = (areaPolygons || [])
      .filter(p => Array.isArray(p) && p.length >= 3);
    if (!keywords.length && !circles.length && !polygons.length) {
      return toast.error('Add at least one keyword, one circle or one polygon');
    }
    try {
      const payload = { name, keywords, circles, polygons };
      if (editingArea) {
        await axios.put(`${API}/pricing-areas/${editingArea.id}`, payload, auth);
        toast.success('Area updated');
      } else {
        await axios.post(`${API}/pricing-areas`, payload, auth);
        toast.success('Area created');
      }
      setAreaDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save area');
    }
  };

  const importKml = async () => {
    const url = kmlUrl.trim();
    if (!url) return toast.error('Paste a Google MyMaps or KML link');
    setKmlImporting(true);
    try {
      const res = await axios.post(
        `${API}/pricing-areas/import-kml`,
        { url, prefix: kmlPrefix.trim() || null, replace_existing: kmlReplaceExisting },
        auth,
      );
      const { created = 0, updated = 0, skipped = 0, total_found = 0 } = res.data || {};
      toast.success(`Imported ${created} new · ${updated} updated · ${skipped} skipped (of ${total_found} polygons)`);
      setKmlDialogOpen(false);
      setKmlUrl('');
      setKmlPrefix('');
      setKmlReplaceExisting(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import KML');
    } finally {
      setKmlImporting(false);
    }
  };

  const deleteArea = async () => {
    if (!areaDeleteId) return;
    try {
      await axios.delete(`${API}/pricing-areas/${areaDeleteId}`, auth);
      toast.success('Area deleted');
      setAreaDeleteId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete area');
      setAreaDeleteId(null);
    }
  };

  // ---------- Rules ----------
  const openRuleDialog = (rule = null) => {
    setEditingRule(rule);
    if (rule) {
      setRuleForm({
        partner_company: rule.partner_company,
        vehicle_category: rule.vehicle_category,
        origin_area_id: rule.origin_area_id,
        destination_area_id: rule.destination_area_id,
        price: String(rule.price ?? ''),
      });
    } else {
      setRuleForm({
        partner_company: partnerCompanies[0] || '',
        vehicle_category: vehicleTypes[0] || 'Sedan',
        origin_area_id: areas[0]?.id || '',
        destination_area_id: areas[0]?.id || '',
        price: '',
      });
    }
    setRuleDialogOpen(true);
  };

  const saveRule = async () => {
    const price = parseFloat(ruleForm.price);
    if (!ruleForm.partner_company) return toast.error('Partner is required');
    if (!ruleForm.vehicle_category) return toast.error('Category is required');
    if (!ruleForm.origin_area_id) return toast.error('Origin area is required');
    if (!ruleForm.destination_area_id) return toast.error('Destination area is required');
    if (!Number.isFinite(price) || price < 0) return toast.error('Enter a valid price');

    const body = { ...ruleForm, price };
    try {
      if (editingRule) {
        await axios.put(`${API}/pricing-rules/${editingRule.id}`, body, auth);
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/pricing-rules`, body, auth);
        toast.success('Rule created');
      }
      setRuleDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rule');
    }
  };

  const deleteRule = async () => {
    if (!ruleDeleteId) return;
    try {
      await axios.delete(`${API}/pricing-rules/${ruleDeleteId}`, auth);
      toast.success('Rule deleted');
      setRuleDeleteId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete rule');
      setRuleDeleteId(null);
    }
  };

  // Filtered rules
  const displayRules = useMemo(() => {
    return rules.filter(r => {
      if (filterPartner !== 'all' && r.partner_company !== filterPartner) return false;
      if (filterCategory !== 'all' && r.vehicle_category !== filterCategory) return false;
      if (search) {
        const s = search.toLowerCase();
        const blob = [
          r.partner_company, r.vehicle_category,
          r.origin_area_name, r.destination_area_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [rules, filterPartner, filterCategory, search]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center text-white">
          <Tag className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pricing Rules</h1>
          <p className="text-sm text-slate-500">Predefined prices per partner, vehicle category and route.</p>
        </div>
      </div>

      <Tabs defaultValue="rules">
        <TabsList className="mb-4">
          <TabsTrigger value="rules" data-testid="tab-pricing-rules">Rules ({rules.length})</TabsTrigger>
          <TabsTrigger value="areas" data-testid="tab-pricing-areas">Areas ({areas.length})</TabsTrigger>
        </TabsList>

        {/* --------- RULES TAB --------- */}
        <TabsContent value="rules" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <Select value={filterPartner} onValueChange={setFilterPartner}>
                <SelectTrigger className="w-[180px]" data-testid="filter-partner">
                  <SelectValue placeholder="Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All partners</SelectItem>
                  {partnerCompanies.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[160px]" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-8 w-[220px]"
                  placeholder="Search route/partner…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="filter-search"
                />
              </div>
            </div>
            <Button
              onClick={() => openRuleDialog()}
              disabled={partnerCompanies.length === 0 || areas.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-rule-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> New rule
            </Button>
          </div>

          {areas.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              You have no areas yet. Create at least two areas (e.g. &quot;Airport&quot; and &quot;Athens Center&quot;) on the Areas tab before adding rules.
            </div>
          )}
          {partnerCompanies.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No partner companies detected yet. Import at least one partner Excel/PDF so their name (e.g. MOZIO, JOURNEY) shows up as a selectable partner here.
            </div>
          )}

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">Partner</th>
                    <th className="px-3 py-2 text-left">Category</th>
                    <th className="px-3 py-2 text-left">Route</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">Loading…</td></tr>
                  )}
                  {!loading && displayRules.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">
                      {rules.length === 0 ? 'No pricing rules yet. Create your first rule.' : 'No rules match the filters.'}
                    </td></tr>
                  )}
                  {displayRules.map(r => (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`rule-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-700">{r.partner_company || '—'}</td>
                      <td className="px-3 py-2"><Badge variant="secondary">{r.vehicle_category}</Badge></td>
                      <td className="px-3 py-2 text-slate-600">
                        <span className="font-medium text-slate-700">{r.origin_area_name}</span>
                        <span className="text-slate-400 mx-1.5">→</span>
                        <span className="font-medium text-slate-700">{r.destination_area_name}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">€{Number(r.price || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openRuleDialog(r)} data-testid={`edit-rule-${r.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setRuleDeleteId(r.id)} data-testid={`delete-rule-${r.id}`}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* --------- AREAS TAB --------- */}
        <TabsContent value="areas" className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-slate-500">
              Areas group locations by keyword, circle or polygon. When a ride is imported or edited,
              its pickup/dropoff (text + coords) is matched against these to pick an area.
            </p>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => setKmlDialogOpen(true)}
                data-testid="import-kml-btn"
              >
                <Globe className="h-4 w-4 mr-1" /> Import from KML
              </Button>
              <Button onClick={() => openAreaDialog()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-area-btn">
                <Plus className="h-4 w-4 mr-1" /> New area
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {loading && <div className="col-span-full py-6 text-center text-slate-400">Loading…</div>}
            {!loading && areas.length === 0 && (
              <div className="col-span-full py-8 text-center text-slate-400">
                No areas yet. Create your first one (e.g. &quot;Airport&quot; with keywords &quot;airport, eleftherios&quot;).
              </div>
            )}
            {areas.map(a => (
              <div key={a.id} className="bg-white rounded-lg border border-slate-200 p-3 hover:shadow-sm transition" data-testid={`area-card-${a.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-emerald-600" />
                    <h3 className="font-semibold text-slate-800">{a.name}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openAreaDialog(a)} data-testid={`edit-area-${a.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setAreaDeleteId(a.id)} data-testid={`delete-area-${a.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(a.keywords || []).length === 0 && (a.circles || []).length === 0 && (a.polygons || []).length === 0 && <span className="text-xs text-slate-400">No keywords, circles or polygons</span>}
                  {(a.keywords || []).map((k, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{k}</Badge>
                  ))}
                  {(a.circles || []).length > 0 && (
                    <Badge variant="secondary" className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <MapPin className="h-3 w-3 mr-1" />
                      {a.circles.length} circle{a.circles.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  {(a.polygons || []).length > 0 && (
                    <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border border-blue-200">
                      <Globe className="h-3 w-3 mr-1" />
                      {a.polygons.length} polygon{a.polygons.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Area Dialog */}
      <Dialog open={areaDialogOpen} onOpenChange={setAreaDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArea ? 'Edit area' : 'New area'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="e.g. Airport"
                data-testid="area-name-input"
              />
            </div>
            <div>
              <Label>Keywords <span className="text-slate-400 font-normal">(comma-separated, optional)</span></Label>
              <Input
                value={areaKeywords}
                onChange={(e) => setAreaKeywords(e.target.value)}
                placeholder="airport, eleftherios, venizelos"
                data-testid="area-keywords-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Any of these words appearing in the pickup/dropoff string will match this area.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Geographic circles <span className="text-slate-400 font-normal">(optional)</span></Label>
                {areaCircles.length > 0 && (
                  <span className="text-xs text-slate-500">{areaCircles.length} circle{areaCircles.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <AreaMapEditor
                circles={areaCircles}
                polygons={areaPolygons}
                onChange={({ circles, polygons }) => { setAreaCircles(circles); setAreaPolygons(polygons); }}
              />
              <p className="text-xs text-slate-400 mt-1">
                Rides whose pickup or dropoff coordinates fall inside any of these circles or polygons will match this area — even if the address string doesn&apos;t match a keyword.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAreaDialogOpen(false)}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={saveArea} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-area-btn">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule Dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit pricing rule' : 'New pricing rule'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Partner *</Label>
              <Select value={ruleForm.partner_company} onValueChange={(v) => setRuleForm({ ...ruleForm, partner_company: v })}>
                <SelectTrigger data-testid="rule-partner-select"><SelectValue placeholder="Partner company…" /></SelectTrigger>
                <SelectContent>
                  {partnerCompanies.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">
                Partner companies extracted from imported rides (the &quot;partner&quot; field on each ride, e.g. MOZIO, JOURNEY).
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Category *</Label>
                <button
                  type="button"
                  onClick={() => { setAddingCategory(true); setNewCategoryInput(''); }}
                  className="text-xs text-emerald-600 hover:underline"
                  data-testid="add-category-inline"
                >
                  + New category
                </button>
              </div>
              {addingCategory ? (
                <div className="flex gap-1">
                  <Input
                    autoFocus
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="e.g. Minibus"
                    onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); if (e.key === 'Escape') { setAddingCategory(false); setNewCategoryInput(''); } }}
                    data-testid="new-category-input"
                    className="h-9"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={addCategory}
                    className="bg-emerald-600 hover:bg-emerald-700 h-9"
                    data-testid="save-new-category-btn"
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => { setAddingCategory(false); setNewCategoryInput(''); }}
                    className="h-9"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Select value={ruleForm.vehicle_category} onValueChange={(v) => setRuleForm({ ...ruleForm, vehicle_category: v })}>
                  <SelectTrigger data-testid="rule-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Price (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={ruleForm.price}
                onChange={(e) => setRuleForm({ ...ruleForm, price: e.target.value })}
                placeholder="0.00"
                data-testid="rule-price-input"
              />
            </div>
            <div>
              <Label>Origin area *</Label>
              <Select value={ruleForm.origin_area_id} onValueChange={(v) => setRuleForm({ ...ruleForm, origin_area_id: v })}>
                <SelectTrigger data-testid="rule-origin-select"><SelectValue placeholder="From…" /></SelectTrigger>
                <SelectContent>
                  {areas.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destination area *</Label>
              <Select value={ruleForm.destination_area_id} onValueChange={(v) => setRuleForm({ ...ruleForm, destination_area_id: v })}>
                <SelectTrigger data-testid="rule-destination-select"><SelectValue placeholder="To…" /></SelectTrigger>
                <SelectContent>
                  {areas.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleDialogOpen(false)}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={saveRule} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-rule-btn">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KML Import Dialog */}
      <Dialog open={kmlDialogOpen} onOpenChange={setKmlDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              Import areas from KML / Google MyMaps
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Google MyMaps link or KML URL *</Label>
              <Input
                value={kmlUrl}
                onChange={(e) => setKmlUrl(e.target.value)}
                placeholder="https://www.google.com/maps/d/embed?mid=..."
                data-testid="kml-url-input"
                noUpperCase
              />
              <p className="text-xs text-slate-500 mt-1">
                Paste the &quot;embed&quot; or &quot;viewer&quot; link. We&apos;ll fetch the KML export
                automatically. Each polygon in the map becomes a separate area.
              </p>
            </div>
            <div>
              <Label>Name prefix <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                value={kmlPrefix}
                onChange={(e) => setKmlPrefix(e.target.value)}
                placeholder="e.g. Journey · "
                data-testid="kml-prefix-input"
                noUpperCase
              />
              <p className="text-xs text-slate-500 mt-1">
                Prefixed to every area name so you can group them per partner.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={kmlReplaceExisting}
                onChange={(e) => setKmlReplaceExisting(e.target.checked)}
                data-testid="kml-replace-toggle"
              />
              Replace polygons on same-named existing areas (otherwise they&apos;re skipped)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKmlDialogOpen(false)} disabled={kmlImporting}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={importKml}
              disabled={kmlImporting || !kmlUrl.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-kml-import-btn"
            >
              <Upload className="h-4 w-4 mr-1" /> {kmlImporting ? 'Importing…' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete area confirm */}
      <AlertDialog open={!!areaDeleteId} onOpenChange={(o) => !o && setAreaDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete area?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. If any pricing rule uses this area, deletion will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteArea} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete rule confirm */}
      <AlertDialog open={!!ruleDeleteId} onOpenChange={(o) => !o && setRuleDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pricing rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Future imports for this partner/category/route will no longer auto-fill the price.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteRule} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
